import { useCallback, useEffect, useRef } from "react";
import { usePostHog } from "@posthog/react";

import { usePostHogEventSender } from "common/contexts/PostHogAnalyticsProvider";
import {
  Feature,
  Features,
  PaymentMethodOption,
  SubscriptionFrequency,
  WalletType,
} from "common/types";
import { toDollarValue } from "common/utils/formatting";
import {
  ClientCartDetail,
  DeliveryGroupKind,
  FreeShippingProgress,
  ICartItem,
} from "common/types/carts";
import { Optional } from "common/utils/types";
import { useIntersectionObserver } from "common/utils/dom";
import { useLatestRef } from "common/hooks";

import { ClientCheckoutOrder } from "client/orders/types";
import type { PersonalizationKind, ProductListSort } from "client/products/types";
import { INITIAL_CLIENT_DATA } from "client/common/utils";

import {
  AnalyticsCartItem,
  AnalyticsItemData,
  AnalyticsItemEventData,
  AnalyticsProductData,
  AnalyticsProductLineData,
  CustomAnalyticsEventName,
  CustomAnalyticsEventType,
  isAnalyticsItemData,
  isAnalyticsProductData,
  EcommerceAddToCartEvent,
  Experiment,
  EcommerceAddShippingInfoEvent,
  EcommerceAddPaymentInfoEvent,
  EcommerceBeginCheckoutEvent,
  EcommerceEventName,
  EcommerceEventType,
  EcommerceItem,
  EcommercePaymentType,
  EcommerceSelectItemEvent,
  EcommerceViewItemEvent,
  EcommerceViewItemListEvent,
  FreeShippingProgressSurface,
  EcommercePurchaseEvent,
  EcommerceRemoveFromCartEvent,
  PageViewData,
  ProductListIdentity,
  ProductLineListSource,
  ProductListSource,
  ClientPostHogEvent,
  AddToCartEventData,
  AddToCartFromLinkEventData,
  BeginCheckoutEventData,
  FreeShippingProgressViewEventData,
  ItemClickEventData,
  LoginAttemptedEventData,
  LoginFailedEventData,
  LoginSucceededEventData,
  PasswordResetRequestFailedEventData,
  PasswordResetRequestSucceededEventData,
  PasswordResetRequestedEventData,
  ProductClickEventData,
  ProductLineClickEventData,
  ProductLineViewEventData,
  ProductLinesViewEventData,
  ProductListFilterChangeEventData,
  ProductListSortChangeEventData,
  ProductsViewEventData,
  PromotionInfoClickEventData,
  RemoveFromCartEventData,
  SignupCodeRequestFailedEventData,
  SignupCodeRequestSucceededEventData,
  SignupCodeRequestedEventData,
  SignupCompletedEventData,
  SignupFailedEventData,
  UpdateCartItemAutoshipEventData,
  UpdateShippingMethodEventData,
  ClientPostHogEventName,
} from "../types";

/**
 * Clinic flags stamped onto every client PostHog event as `$feature/<flag>`, so funnels can be
 * broken down by rollout. Must match marketing.constants.POSTHOG_EXPERIMENT_FLAGS: a flag listed
 * on one side only splits its own funnel.
 */
export const POSTHOG_EXPERIMENT_FLAGS: Feature[] = [Feature.FreeShippingProgress];

/**
 * The shopper's PostHog experiment variants as super properties, `{ "$feature/<key>": variant }`,
 * so every event can be broken down by variant.
 */
export const experimentProperties = (
  experiments: Partial<Record<Experiment, string>> = {},
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(experiments).map(([key, variant]) => [`$feature/${key}`, variant]),
  );

export const getAddToCartAutoshipFields = (
  subscription: Optional<Pick<SubscriptionFrequency, "frequency" | "interval_unit">>,
): Pick<AddToCartEventData, "is_autoship" | "frequency" | "interval_unit"> => ({
  is_autoship: Boolean(subscription),
  frequency: subscription?.frequency ?? null,
  interval_unit: subscription?.interval_unit ?? null,
});

/**
 * Clinic feature flags as PostHog experiment super properties, `"test"` when the flag is on:
 * `{ "$feature/<flag>": "test" | "control" }`. Those are PostHog's default variant names.
 */
export const featureFlagProperties = (
  features: Features,
  ...flags: Feature[]
): Record<string, "test" | "control"> =>
  Object.fromEntries(
    flags.map((flag): [string, "test" | "control"] => [
      `$feature/${flag}`,
      features[flag] ? "test" : "control",
    ]),
  );

export const getCartItemPricing = (cartItem: ICartItem): { price: number; discount: number } => ({
  price: cartItem.custom_unit_price ?? cartItem.default_unit_price,
  discount: cartItem.unit_discount,
});

/** Builds pricing fields from optional price/discount values. */
const pricingFields = (opts?: { price?: number; discount?: number }): Partial<EcommerceItem> => ({
  ...(opts?.price != null ? { price: toDollarValue(opts.price) } : {}),
  ...(opts?.discount != null ? { discount: toDollarValue(opts.discount) } : {}),
});

const productLineFields = (pl: AnalyticsProductLineData): Partial<EcommerceItem> => ({
  item_id: `${pl.id}`,
  item_name: pl.name,
  item_brand: pl.brand_name,
  item_category: pl.primary_category_name,
  item_category2: pl.categories
    .filter((c) => c.name !== pl.primary_category_name)
    .map((c) => c.name)
    .sort()
    .join(", "),
  prescription_required: pl.prescription_required,
  ...(pl.manufacturer ? { manufacturer_name: pl.manufacturer.name } : {}),
});

const productFields = (product: AnalyticsProductData): Partial<EcommerceItem> => ({
  ...productLineFields(product.product_line),
  product_id: `${product.id}`,
  product_name: product.name,
});

const itemFields = (data: AnalyticsItemData): Partial<EcommerceItem> => ({
  ...productFields(data.product),
  vetcove_item_id: `${data.id}`,
  vetcove_item_name: data.name,
  days_supply: data.days_supply,
  frequency: `${data.duration_value} ${data.duration_unit}`,
});

const toEcommerceItem = (
  data: AnalyticsItemEventData,
  opts?: { price?: number; discount?: number },
): EcommerceItem => {
  if (isAnalyticsItemData(data)) {
    return { ...itemFields(data), ...pricingFields(opts) };
  }
  if (isAnalyticsProductData(data)) {
    return { ...productFields(data), ...pricingFields(opts) };
  }
  return { ...productLineFields(data), ...pricingFields(opts) };
};

/**
 * Converts an AnalyticsCartItem to EcommerceItem.
 * Uses pre-computed price and discount from the cart item.
 */
const analyticsCartItemToEcommerceItem = (cartItem: AnalyticsCartItem): EcommerceItem => ({
  ...toEcommerceItem(cartItem.item, { price: cartItem.price, discount: cartItem.discount }),
  quantity: cartItem.quantity,
});

const initialClientData = INITIAL_CLIENT_DATA;

/**
 * Sends GA4 ecommerce events via `gtag()`. Events are processed by the gtag.js
 * library into the eventModel format GA4 and GTM tags expect — including the
 * nested ecommerce item shape — so prefer this over `sendDataLayerEvent` for
 * anything GA4 recognizes natively.
 *
 * Assumes the partner has gtag.js fully loaded for the clinic, either by
 * providing a GA measurement ID (loaded by c15t in ConsentProvider) or by
 * loading gtag as a tag inside their Google Tag Manager container. The stub
 * in react-bundle.html queues calls made before gtag.js initializes; gtag.js
 * replays them once it loads. Clinics without either path will silently drop
 * these events.
 *
 * For custom (non-GA4) events — e.g. page_view virtual pageviews — use
 * `sendDataLayerEvent` instead, which pushes to the dataLayer directly and does
 * not require gtag.js.
 *
 * Event names and data are defined by our partners' GTM setup.
 * Keep consistent with: https://docs.google.com/document/d/1sYGNv_llIEIvoFTq2Z9fX12oGn3BT46NgAlDYM0LsI0/
 * */
export const sendGtagEvent = <T extends EcommerceEventType<EcommerceEventName, object>>({
  name,
  data,
}: T) => {
  if (initialClientData.debug_analytics_events) {
    console.log(`[Analytics][GTM] ${name}`, data); // eslint-disable-line no-console
  }
  gtag("event", name, data);
};

/**
 * Sends custom (non-GA4) analytics events by pushing directly to the dataLayer.
 * The event name is prefixed with `vetcove_` (e.g. `page_view` →
 * `vetcove_page_view`) so it cannot collide with GA4 reserved event names or
 * other tags sharing the container.
 *
 * Unlike `sendGtagEvent`, this does not depend on gtag.js being loaded —
 * GTM reads the pushed object directly — making it the right choice for events
 * that partners wire up through GTM triggers rather than GA4 ecommerce.
 */
export const sendDataLayerEvent = <
  T extends CustomAnalyticsEventType<CustomAnalyticsEventName, object>,
>({
  name,
  data,
}: T) => {
  const namespacedEventName = `vetcove_${name}`;
  if (initialClientData.debug_analytics_events) {
    console.log(`[Analytics][GTM] ${namespacedEventName}`, data); // eslint-disable-line no-console
  }
  window.dataLayer.push({ event: namespacedEventName, ...data });
};

/**
 * Returns a callback that sends a client (pet parent) app PostHog event, named by
 * `ClientPostHogEventName`. Use it, or one of the event-specific `useSendPostHog*` hooks built on
 * it, for events in the client app; clinic app events use `useSendClinicPostHogEvent`.
 */
export const useSendClientPostHogEvent = (): ((event: ClientPostHogEvent) => void) =>
  usePostHogEventSender<ClientPostHogEventName>({
    isEnabled: initialClientData.has_internal_analytics_tracking,
    isDebugEnabled: initialClientData.debug_analytics_events,
  });

/**
 * Returns the current PostHog distinct id, or null when analytics is disabled.
 *
 * Used to hand a logged-out requester's anonymous distinct id to the backend so it can be
 * aliased to the client's identified id, joining pre- and post-identification events.
 */
export const useGetPostHogDistinctId = () => {
  const posthog = usePostHog();

  return useCallback((): string | null => {
    if (!initialClientData.has_internal_analytics_tracking || !window.POSTHOG_PUBLIC_API_KEY) {
      return null;
    }
    return posthog.get_distinct_id();
  }, [posthog]);
};

const exposedExperiments = new Set<Experiment>();

/**
 * Reports the shopper's exposure to an experiment once per page load. Call it from the component
 * the experiment changes, rendered the same way in every variant, or one variant counts shoppers
 * the other never saw.
 */
export const useExperimentExposure = (experiment: Experiment) => {
  const sendPostHogEvent = useSendClientPostHogEvent();
  const variant = initialClientData.experiments?.[experiment];

  useEffect(() => {
    if (!variant || exposedExperiments.has(experiment)) {
      return;
    }
    exposedExperiments.add(experiment);
    const event: ClientPostHogEvent = {
      name: ClientPostHogEventName.FeatureFlagCalled,
      data: { $feature_flag: experiment, $feature_flag_response: variant },
    };
    sendPostHogEvent(event);
  }, [experiment, variant, sendPostHogEvent]);
};

export const sendPageView = (data: PageViewData) => {
  sendDataLayerEvent({ name: CustomAnalyticsEventName.PageView, data });
};

export const useSendPostHogLoginAttempted = () => {
  const sendPostHogEvent = useSendClientPostHogEvent();

  return useCallback(
    (eventData: LoginAttemptedEventData) => {
      sendPostHogEvent({ name: ClientPostHogEventName.LoginAttempted, data: eventData });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogLoginFailed = () => {
  const sendPostHogEvent = useSendClientPostHogEvent();

  return useCallback(
    (eventData: LoginFailedEventData) => {
      sendPostHogEvent({ name: ClientPostHogEventName.LoginFailed, data: eventData });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogLoginSucceeded = () => {
  const sendPostHogEvent = useSendClientPostHogEvent();

  return useCallback(
    (eventData: LoginSucceededEventData) => {
      sendPostHogEvent({ name: ClientPostHogEventName.LoginSucceeded, data: eventData });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogPasswordResetRequested = () => {
  const sendPostHogEvent = useSendClientPostHogEvent();

  return useCallback(
    (eventData: PasswordResetRequestedEventData) => {
      sendPostHogEvent({ name: ClientPostHogEventName.PasswordResetRequested, data: eventData });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogPasswordResetRequestSucceeded = () => {
  const sendPostHogEvent = useSendClientPostHogEvent();

  return useCallback(
    (eventData: PasswordResetRequestSucceededEventData) => {
      sendPostHogEvent({
        name: ClientPostHogEventName.PasswordResetRequestSucceeded,
        data: eventData,
      });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogPasswordResetRequestFailed = () => {
  const sendPostHogEvent = useSendClientPostHogEvent();

  return useCallback(
    (eventData: PasswordResetRequestFailedEventData) => {
      sendPostHogEvent({
        name: ClientPostHogEventName.PasswordResetRequestFailed,
        data: eventData,
      });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogSignupCodeRequested = () => {
  const sendPostHogEvent = useSendClientPostHogEvent();

  return useCallback(
    (eventData: SignupCodeRequestedEventData) => {
      sendPostHogEvent({ name: ClientPostHogEventName.SignupCodeRequested, data: eventData });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogSignupCodeRequestSucceeded = () => {
  const sendPostHogEvent = useSendClientPostHogEvent();

  return useCallback(
    (eventData: SignupCodeRequestSucceededEventData) => {
      sendPostHogEvent({
        name: ClientPostHogEventName.SignupCodeRequestSucceeded,
        data: eventData,
      });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogSignupCodeRequestFailed = () => {
  const sendPostHogEvent = useSendClientPostHogEvent();

  return useCallback(
    (eventData: SignupCodeRequestFailedEventData) => {
      sendPostHogEvent({ name: ClientPostHogEventName.SignupCodeRequestFailed, data: eventData });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogSignupCompleted = () => {
  const sendPostHogEvent = useSendClientPostHogEvent();

  return useCallback(
    (eventData: SignupCompletedEventData) => {
      sendPostHogEvent({ name: ClientPostHogEventName.SignupCompleted, data: eventData });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogSignupFailed = () => {
  const sendPostHogEvent = useSendClientPostHogEvent();

  return useCallback(
    (eventData: SignupFailedEventData) => {
      sendPostHogEvent({ name: ClientPostHogEventName.SignupFailed, data: eventData });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogAddToCart = () => {
  const sendPostHogEvent = useSendClientPostHogEvent();

  return useCallback(
    (eventData: AddToCartEventData) => {
      sendPostHogEvent({
        name: ClientPostHogEventName.AddToCart,
        data: eventData,
      });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogRemoveFromCart = () => {
  const sendPostHogEvent = useSendClientPostHogEvent();

  return useCallback(
    (eventData: RemoveFromCartEventData) => {
      sendPostHogEvent({
        name: ClientPostHogEventName.RemoveFromCart,
        data: eventData,
      });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogUpdateCartItemAutoship = () => {
  const sendPostHogEvent = useSendClientPostHogEvent();

  return useCallback(
    (eventData: UpdateCartItemAutoshipEventData) => {
      sendPostHogEvent({
        name: ClientPostHogEventName.UpdateCartItemAutoship,
        data: eventData,
      });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogProductLineClick = () => {
  const sendPostHogEvent = useSendClientPostHogEvent();

  return useCallback(
    (eventData: ProductLineClickEventData) => {
      sendPostHogEvent({
        name: ClientPostHogEventName.ProductLineClick,
        data: eventData,
      });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogProductListSortChange = () => {
  const sendPostHogEvent = useSendClientPostHogEvent();

  return useCallback(
    (eventData: ProductListSortChangeEventData) => {
      sendPostHogEvent({
        name: ClientPostHogEventName.ProductListSortChange,
        data: eventData,
      });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogProductListFilterChange = () => {
  const sendPostHogEvent = useSendClientPostHogEvent();

  return useCallback(
    (eventData: ProductListFilterChangeEventData) => {
      sendPostHogEvent({
        name: ClientPostHogEventName.ProductListFilterChange,
        data: eventData,
      });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogPromotionInfoClick = () => {
  const sendPostHogEvent = useSendClientPostHogEvent();

  return useCallback(
    (eventData: PromotionInfoClickEventData) => {
      sendPostHogEvent({
        name: ClientPostHogEventName.PromotionInfoClick,
        data: eventData,
      });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogProductClick = () => {
  const sendPostHogEvent = useSendClientPostHogEvent();

  return useCallback(
    (eventData: ProductClickEventData) => {
      sendPostHogEvent({
        name: ClientPostHogEventName.ProductClick,
        data: eventData,
      });
    },
    [sendPostHogEvent],
  );
};

/**
 * Minimal item identifiers for the PostHog item_click event. Kept separate from
 * the GA4 ecommerce AnalyticsItemData so the PostHog and ecommerce item shapes
 * can evolve independently.
 */
type ItemClickData = {
  id: number;
  product: {
    id: number;
    product_line: { id: number };
  };
};

export const useSendPostHogItemClick = () => {
  const sendPostHogEvent = useSendClientPostHogEvent();

  return useCallback(
    (item: ItemClickData, opts: Pick<ItemClickEventData, "source" | "unit_price">) => {
      sendPostHogEvent({
        name: ClientPostHogEventName.ItemClick,
        data: {
          source: opts.source,
          id: item.id,
          product_id: item.product.id,
          product_line_id: item.product.product_line.id,
          unit_price: opts.unit_price,
        },
      });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogProductsView = () => {
  const sendPostHogEvent = useSendClientPostHogEvent();

  return useCallback(
    (eventData: ProductsViewEventData) => {
      sendPostHogEvent({
        name: ClientPostHogEventName.ProductsView,
        data: eventData,
      });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogAddToCartFromLink = () => {
  const sendPostHogEvent = useSendClientPostHogEvent();

  return useCallback(
    (eventData: AddToCartFromLinkEventData) => {
      sendPostHogEvent({
        name: ClientPostHogEventName.AddToCartFromLink,
        data: eventData,
      });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogProductLineView = () => {
  const sendPostHogEvent = useSendClientPostHogEvent();

  return useCallback(
    (eventData: ProductLineViewEventData) => {
      sendPostHogEvent({
        name: ClientPostHogEventName.ProductLineView,
        data: eventData,
      });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogProductLinesView = () => {
  const sendPostHogEvent = useSendClientPostHogEvent();

  return useCallback(
    (eventData: ProductLinesViewEventData) => {
      sendPostHogEvent({
        name: ClientPostHogEventName.ProductLinesView,
        data: eventData,
      });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogFreeShippingProgressView = () => {
  const sendPostHogEvent = useSendClientPostHogEvent();

  return useCallback(
    (eventData: FreeShippingProgressViewEventData) => {
      sendPostHogEvent({
        name: ClientPostHogEventName.FreeShippingProgressView,
        data: eventData,
      });
    },
    [sendPostHogEvent],
  );
};

type BuildFreeShippingProgressViewDataParams = {
  cartId: number;
  freeShipping: FreeShippingProgress;
  surface: FreeShippingProgressSurface;
};

/** The free_shipping_progress_view payload for a banner as it came into view */
export const buildFreeShippingProgressViewData = ({
  cartId,
  freeShipping,
  surface,
}: BuildFreeShippingProgressViewDataParams): FreeShippingProgressViewEventData => ({
  cart_id: cartId,
  surface,
  status: freeShipping.status,
  amount_remaining: freeShipping.amount_remaining,
  paid_shipment_count: freeShipping.paid_shipments.length,
});

/** The cart fields that tell one banner view from another */
type FreeShippingProgressViewCart = Pick<ClientCartDetail, "id" | "client_id">;

/**
 * Identifies one banner view. The client is part of it because a session cart can carry over
 * when a visitor logs in, and a logged-in view should still count.
 */
export const getFreeShippingProgressViewKey = (
  cart: FreeShippingProgressViewCart,
  surface: FreeShippingProgressSurface,
) => `${cart.id}:${cart.client_id}:${surface}`;

/** Keys from getFreeShippingProgressViewKey already sent this page session */
const viewedFreeShippingProgressKeys = new Set<string>();

type UseFreeShippingProgressViewOnVisibleOpts = {
  cart: FreeShippingProgressViewCart;
  freeShipping: FreeShippingProgress;
  surface: FreeShippingProgressSurface;
};

/**
 * Sends one free_shipping_progress_view per cart, client and surface for the page session, the
 * first time the banner scrolls into view. Reopening the drawer or revisiting a step doesn't
 * refire.
 */
export const useFreeShippingProgressViewOnVisible = ({
  cart,
  freeShipping,
  surface,
}: UseFreeShippingProgressViewOnVisibleOpts) => {
  const sendPostHogFreeShippingProgressView = useSendPostHogFreeShippingProgressView();
  const ref = useRef<HTMLDivElement>(null);
  // Read through a ref so a cart refetch doesn't rebuild the observer
  const freeShippingRef = useLatestRef(freeShipping);
  const viewKey = getFreeShippingProgressViewKey(cart, surface);
  const cartId = cart.id;

  const onIntersect = useCallback(() => {
    if (viewedFreeShippingProgressKeys.has(viewKey)) {
      return;
    }
    viewedFreeShippingProgressKeys.add(viewKey);
    sendPostHogFreeShippingProgressView(
      buildFreeShippingProgressViewData({
        cartId,
        freeShipping: freeShippingRef.current,
        surface,
      }),
    );
  }, [viewKey, cartId, freeShippingRef, surface, sendPostHogFreeShippingProgressView]);

  useIntersectionObserver(ref, onIntersect);
  return ref;
};

export const getBeginCheckoutFreeShippingFields = (
  cart: Pick<ClientCartDetail, "free_shipping">,
): Pick<BeginCheckoutEventData, "free_shipping_status" | "free_shipping_amount_remaining"> => ({
  free_shipping_status: cart.free_shipping?.status ?? null,
  free_shipping_amount_remaining: cart.free_shipping?.amount_remaining ?? null,
});

export const useSendPostHogBeginCheckout = () => {
  const sendPostHogEvent = useSendClientPostHogEvent();

  return useCallback(
    (cart: ClientCartDetail) => {
      const event: ClientPostHogEvent = {
        name: ClientPostHogEventName.BeginCheckout,
        data: {
          cart_id: cart.id,
          client_id: cart.client_id,
          clinic_id: cart.clinic.id,
          subtotal: cart.subtotal,
          discount: cart.discount,
          shipping: cart.shipping,
          total: cart.total,
          promo_code_ids: cart.promo_codes.map((promoCode) => promoCode.id),
          ...getBeginCheckoutFreeShippingFields(cart),
          cart_items: cart.items.map((cartItem) => {
            const { price, discount } = getCartItemPricing(cartItem);
            return {
              id: cartItem.id,
              item_id: cartItem.item.id,
              patient_id: cartItem.patient?.id ?? null,
              flavor_selection_id: cartItem.flavor_selection ?? null,
              quantity: cartItem.quantity,
              unit_price: price,
              discount,
            };
          }),
        },
      };
      sendPostHogEvent(event);
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogUpdateShippingMethod = () => {
  const sendPostHogEvent = useSendClientPostHogEvent();

  return useCallback(
    (eventData: UpdateShippingMethodEventData) => {
      sendPostHogEvent({
        name: ClientPostHogEventName.UpdateShippingMethod,
        data: eventData,
      });
    },
    [sendPostHogEvent],
  );
};

export const useSendPostHogCheckoutCompleted = () => {
  const sendPostHogEvent = useSendClientPostHogEvent();

  return useCallback(
    (order: ClientCheckoutOrder, cart: ClientCartDetail) => {
      const event: ClientPostHogEvent = {
        name: ClientPostHogEventName.CheckoutCompleted,
        data: {
          id: order.id,
          cart_id: cart.id,
          client_id: cart.client_id,
          clinic_id: cart.clinic.id,
          payment_method_id: cart.payment_method?.id ?? null,
          shipping_address_id: order.shipping_address?.id ?? null,
          total: order.total,
          discounted_subtotal: order.discounted_subtotal,
          tax: order.tax,
          shipping: order.shipping,
          promo_code_ids: cart.promo_codes.map((promoCode) => promoCode.id),
          order_items: order.order_items.map((orderItem) => ({
            id: orderItem.id,
            item_id: orderItem.item.id,
            patient_id: orderItem.patient?.id ?? null,
            prescription_fill_id: orderItem.prescription_fill?.id ?? null,
            quantity: orderItem.quantity,
            unit_price: orderItem.unit_price,
            amortized_discount: orderItem.amortized_discount,
            status: orderItem.status ?? null,
          })),
        },
      };
      sendPostHogEvent(event);
    },
    [sendPostHogEvent],
  );
};

export const sendPurchase = (order: ClientCheckoutOrder, cart: ClientCartDetail) => {
  const data: EcommercePurchaseEvent = {
    name: EcommerceEventName.Purchase,
    data: {
      currency: "USD",
      transaction_id: `${order.id}`,
      value: toDollarValue(order.discounted_subtotal),
      shipping: toDollarValue(order.shipping),
      tax: toDollarValue(order.tax),
      items: order.order_items.map((orderItem) => ({
        ...toEcommerceItem(orderItem.item, {
          price: orderItem.unit_price,
          discount: orderItem.amortized_discount,
        }),
        quantity: orderItem.quantity,
      })),
      coupon: cart.promo_codes.map((pc) => pc.code).join(", "),
    },
  };
  sendGtagEvent(data);
};

export const sendAddToCart = (cartItem: AnalyticsCartItem) => {
  const item = analyticsCartItemToEcommerceItem(cartItem);
  const data: EcommerceAddToCartEvent = {
    name: EcommerceEventName.AddToCart,
    data: {
      currency: "USD",
      value: toDollarValue(cartItem.price * cartItem.quantity),
      items: [item],
    },
  };
  sendGtagEvent(data);
};

export const sendRemoveFromCart = (cartItem: AnalyticsCartItem) => {
  const item = analyticsCartItemToEcommerceItem(cartItem);
  const data: EcommerceRemoveFromCartEvent = {
    name: EcommerceEventName.RemoveFromCart,
    data: {
      currency: "USD",
      value: toDollarValue(cartItem.price * cartItem.quantity),
      items: [item],
    },
  };
  sendGtagEvent(data);
};

export const sendViewItem = (data: AnalyticsItemEventData, opts: { price: number }) => {
  const item = toEcommerceItem(data, opts);
  const eventData: EcommerceViewItemEvent = {
    name: EcommerceEventName.ViewItem,
    data: {
      currency: "USD",
      value: toDollarValue(opts.price),
      items: [item],
    },
  };
  sendGtagEvent(eventData);
};

const GA4_LIST_NAMES: Partial<Record<string, ProductLineListSource>> = {
  [ProductLineListSource.HomePageForYou]: ProductLineListSource.HomePageFeatured,
  [ProductLineListSource.BrowseCatalogForYou]: ProductLineListSource.BrowseCatalogFeatured,
};

/** Partners' GTM setups key on list names, so a personalized row reports as the row it replaces. */
export const toGa4ListName = (listName: string): string => GA4_LIST_NAMES[listName] ?? listName;

export const sendViewItemList = (items: AnalyticsItemEventData[], listName: string) => {
  const data: EcommerceViewItemListEvent = {
    name: EcommerceEventName.ViewItemList,
    data: {
      item_list_name: toGa4ListName(listName),
      items: items.map((item, i) => ({
        ...toEcommerceItem(item),
        index: i,
      })),
    },
  };
  sendGtagEvent(data);
};

/**
 * Fires a `view_item_list` GA4 event when the referenced element scrolls into view.
 * The event re-fires whenever the set of item IDs or the list name changes.
 *
 * @returns A ref to attach to the container element that should trigger the event.
 */
export const useViewItemListOnVisible = (
  items: Optional<AnalyticsItemEventData[]>,
  listName: string,
) => {
  const ref = useRef<HTMLDivElement>(null);
  const firedRef = useRef(false);

  const itemKey = items?.map((item) => item.id).join(",") ?? "";

  useEffect(() => {
    firedRef.current = false;
  }, [itemKey, listName]);

  const onIntersect = useCallback(() => {
    if (!firedRef.current && items?.length) {
      firedRef.current = true;
      sendViewItemList(items, listName);
    }
  }, [items, listName]);
  useIntersectionObserver(ref, onIntersect);
  return ref;
};

/** Minimal product identifiers for the PostHog products_view event. */
type ProductViewData = {
  id: number;
};

/** What the list a card sits in is, for the events it fires. `collectionSlug` and `categoryId`
 * reach the products_view and every product_click and add_to_cart; the rest describe the view
 * alone. */
export type ProductListContext = {
  searchTerm?: string;
  collectionSlug?: string;
  promotionId?: number;
  categoryId?: number;
  numResults?: number;
  latencyMs?: number;
  sort?: ProductListSort;
};

/** The list's identifiers for a list-level event, so sorts and filters join to the view they
 * changed. Search terms match the backend, which searches on `q.lower().strip()`. */
export const getProductListIdentity = ({
  searchTerm,
  collectionSlug,
  categoryId,
  promotionId,
}: ProductListContext = {}): ProductListIdentity => {
  const normalizedSearchTerm = searchTerm?.toLowerCase().trim();
  return {
    ...(normalizedSearchTerm ? { search_term: normalizedSearchTerm } : {}),
    ...(collectionSlug ? { collection_slug: collectionSlug } : {}),
    ...(categoryId ? { category_id: categoryId } : {}),
    ...(promotionId ? { promotion_id: promotionId } : {}),
  };
};

export const useProductsViewOnVisible = (
  products: Optional<ProductViewData[]>,
  source: ProductListSource,
  options?: ProductListContext,
) => {
  const { searchTerm, collectionSlug, promotionId, categoryId, numResults, latencyMs, sort } =
    options ?? {};
  const sendPostHogProductsView = useSendPostHogProductsView();
  const ref = useRef<HTMLDivElement>(null);
  const firedRef = useRef(false);

  const productKey = products?.map((product) => product.id).join(",") ?? "";

  useEffect(() => {
    firedRef.current = false;
  }, [productKey, source]);

  const onIntersect = useCallback(() => {
    if (!firedRef.current && products?.length) {
      firedRef.current = true;
      sendPostHogProductsView({
        source,
        product_ids: products.map((product) => product.id),
        ...getProductListIdentity({ searchTerm, collectionSlug, categoryId, promotionId }),
        ...(numResults === undefined ? {} : { num_results: numResults }),
        ...(latencyMs === undefined ? {} : { latency_ms: latencyMs }),
        ...(sort ? { sort } : {}),
      });
    }
  }, [
    products,
    sendPostHogProductsView,
    source,
    searchTerm,
    collectionSlug,
    categoryId,
    promotionId,
    numResults,
    latencyMs,
    sort,
  ]);

  useIntersectionObserver(ref, onIntersect);
  return ref;
};

type ProductLineViewData = {
  id: number;
  personalization_kind?: Optional<PersonalizationKind>;
};

export const useProductLinesViewOnVisible = (
  productLines: Optional<ProductLineViewData[]>,
  source: ProductLineListSource,
  categoryId?: number,
) => {
  const sendPostHogProductLinesView = useSendPostHogProductLinesView();
  const ref = useRef<HTMLDivElement>(null);
  const firedRef = useRef(false);

  const productLineKey = productLines?.map((productLine) => productLine.id).join(",") ?? "";

  useEffect(() => {
    firedRef.current = false;
  }, [productLineKey, source]);

  const onIntersect = useCallback(() => {
    if (!firedRef.current && productLines?.length) {
      firedRef.current = true;
      sendPostHogProductLinesView({
        source,
        product_line_ids: productLines.map((productLine) => productLine.id),
        personalization_kinds: productLines.map(
          (productLine) => productLine.personalization_kind ?? null,
        ),
        category_id: categoryId ?? null,
      });
    }
  }, [categoryId, productLines, sendPostHogProductLinesView, source]);

  useIntersectionObserver(ref, onIntersect);
  return ref;
};

/**
 * @param price — included for backwards compatibility with cart item clicks.
 *   TODO: Consider removing price for events where it doesn't make sense
 *   (e.g., product line card clicks, search result clicks).
 */
export const sendSelectItem = (data: AnalyticsItemEventData, listName: string, price?: number) => {
  const item = toEcommerceItem(data, price != null ? { price, discount: 0 } : undefined);
  const eventData: EcommerceSelectItemEvent = {
    name: EcommerceEventName.SelectItem,
    data: {
      item_list_name: toGa4ListName(listName),
      items: [item],
    },
  };
  sendGtagEvent(eventData);
};

/** Resolves ICartItem pricing and converts to EcommerceItem in one step. */
const cartItemToEcommerceItem = (cartItem: ICartItem): EcommerceItem => {
  const { price, discount } = getCartItemPricing(cartItem);
  return {
    ...toEcommerceItem(cartItem.item, { price, discount }),
    quantity: cartItem.quantity,
  };
};

export const sendBeginCheckout = (cart: ClientCartDetail) => {
  const items = cart.items.map(cartItemToEcommerceItem);
  const value = cart.items.reduce((sum, ci) => {
    const { price } = getCartItemPricing(ci);
    return sum + toDollarValue(price) * ci.quantity;
  }, 0);
  const data: EcommerceBeginCheckoutEvent = {
    name: EcommerceEventName.BeginCheckout,
    data: {
      currency: "USD",
      value,
      items,
    },
  };
  sendGtagEvent(data);
};

export const sendAddShippingInfo = (cart: ClientCartDetail) => {
  const coupon = cart.promo_codes.map((pc) => pc.code).join(", ");

  cart.delivery_groups
    .filter((g) => g.kind === DeliveryGroupKind.Shipped)
    .forEach((group) => {
      const option = group.selected_delivery_option;
      if (!option) {
        return;
      }

      const data: EcommerceAddShippingInfoEvent = {
        name: EcommerceEventName.AddShippingInfo,
        data: {
          currency: "USD",
          value: toDollarValue(cart.subtotal),
          ...(coupon ? { coupon } : {}),
          shipping_tier: option.title,
          items: group.cart_items.map((ci) => cartItemToEcommerceItem(ci)),
        },
      };
      sendGtagEvent(data);
    });
};

const getPaymentType = (paymentMethod: PaymentMethodOption): EcommercePaymentType => {
  if (paymentMethod.is_klarna) {
    return "klarna";
  }
  if (paymentMethod.wallet === WalletType.APPLE) {
    return "apple_pay";
  }
  if (paymentMethod.wallet === WalletType.GOOGLE) {
    return "google_pay";
  }
  return "credit_card";
};

export const useSendPostHogUpdatePaymentMethod = () => {
  const sendPostHogEvent = useSendClientPostHogEvent();

  return useCallback(
    (cart: ClientCartDetail, paymentMethod: PaymentMethodOption) => {
      const event: ClientPostHogEvent = {
        name: ClientPostHogEventName.UpdatePaymentMethod,
        data: {
          cart_id: cart.id,
          client_id: cart.client_id,
          clinic_id: cart.clinic.id,
          payment_method_id: typeof paymentMethod.id === "number" ? paymentMethod.id : null,
          payment_type: getPaymentType(paymentMethod),
          wallet_type: paymentMethod.wallet || null,
          card_brand: "brand" in paymentMethod ? paymentMethod.brand : null,
        },
      };
      sendPostHogEvent(event);
    },
    [sendPostHogEvent],
  );
};

export const sendAddPaymentInfo = (cart: ClientCartDetail, paymentMethod: PaymentMethodOption) => {
  const coupon = cart.promo_codes.map((pc) => pc.code).join(", ");

  const data: EcommerceAddPaymentInfoEvent = {
    name: EcommerceEventName.AddPaymentInfo,
    data: {
      currency: "USD",
      value: toDollarValue(cart.subtotal),
      ...(coupon ? { coupon } : {}),
      payment_type: getPaymentType(paymentMethod),
      items: cart.items.map((ci) => cartItemToEcommerceItem(ci)),
    },
  };
  sendGtagEvent(data);
};
